/* eslint-disable no-nested-ternary */
import React, { useMemo, useRef, useState } from 'react';
import moment from 'moment';
import { Row, Col, notification, Button, Drawer } from 'antd';
import {
    StepItemContent,
    StepItemWrapper,
    StepProgressLine,
    StepsWrapper,
    StepsContainer,
    FlightCardsWrapper,
    FareWrapper,
    BestPriceTag,
    RecommendedTag,
    Tag,
    TagsWrapper
} from '../AvailableFlights/styles';
import { LIGHT_PRIMARY } from '../../utils/colors';
import {
    StyledParagraph, StyledTitle,
} from '../CustomAntdComponents/Typography';
import { formatSelectedSeats, getBusTypeImage, getRoadTransportLogo } from './helper';
import { formatNumberAsCurrency } from '../../utils/helpers';
import { ReactComponent as BusIcon } from '../../assets/images/bus-grey.svg';
import { ReactComponent as PassengerIcon } from '../../assets/images/passenger.svg';
import { ReactComponent as ClockIcon } from '../../assets/images/clock.svg';
import { ReactComponent as BestPriceIcon } from '../../assets/images/best-price-icon.svg';
import { ReactComponent as RecommendedIcon } from '../../assets/images/recommended-icon.svg';

import useIsMobile from "../../hooks/useIsMobile";
import { CentralizedContent, SpaceBetweenAlignContent } from "../CustomAntdComponents/Utitlities";

import {
    BusCard,
    BusCardContent,
    BusCardImage,
    BusCardPriceContent, BusDetailsWrapper,
    BusImage,
    BusStepWrapper, SeatFooterWrapper,
    SeatsNumberWrapper,
    MobileBusCard,
    MobileItineraryRow,
    MobileItineraryStation,
    MobileItineraryLine,
    MobileCardFooter,
    MobileOperatorLogo,
    MobilePill
} from "./styles";
import translations from "../../screens/SearchRoadListings/translations";
import { useI18nContext } from "../../utils/I18n";
import { useRoadSearchStore } from "../../store/roadSearch/roadSearchStore";
import SeatSelection from "../SeatSelection/SeatSelection";

type Props = {
    result: BusResultItem,
    handleSelectBus: (arg: BusResultItem, arg2: AvailableBus, arg3: AvailableSeat[]) => void,
    onlyDepartureSelected: boolean,
    cheapestFare: number | null,
    isReturning: boolean,
}

function BusDetails({ result, onlyDepartureSelected, handleSelectBus, cheapestFare, isReturning }: Props) {
    const { state: { t } } = useI18nContext();
    const drawerContainerRef = useRef(null);
    const [selectedSeats, setSelectedSeats] = useState<AvailableSeat[]>([]);
    const [isOpened, setIsOpened] = useState<boolean>(false);
    const [activeBus, setActiveBus] = useState<AvailableBus>();
    const { isMobile } = useIsMobile();

    const {
        formData: { numberOfAdults },
        updateFormData
    } = useRoadSearchStore();

    const disableButton = useMemo(() => {
        if (!isReturning) {
            return selectedSeats.length === 0;
        }

        return numberOfAdults > selectedSeats.length;
    }, [numberOfAdults, selectedSeats]);

    const totalFare = useMemo(() => {
        if (!activeBus) {
            return 0;
        }

        const numberOfSeats = selectedSeats.length;

        return numberOfSeats * activeBus.TotalFare;
    }, [selectedSeats, activeBus]);

    const openNotification = () => {
        notification.open({
            message: 'Departure bus selected',
            placement: 'bottom',
            type: 'success',
        });
    };

    const selectedSeatNumbers = useMemo(() => {
        return selectedSeats.map(seat => seat.SeatNumber);
    }, [selectedSeats]);

    const onBusClick = (bus: AvailableBus) => {
         setActiveBus({ ...bus, AvailableSeats: bus.AvailableSeats.sort((a, b) => a.Row - b.Row) });
        // setActiveBus({...bus, AvailableSeats: bus.AvailableSeats.sort((a, b) => a.Row - b.Row || parseInt(a.SeatNumber) - parseInt( b.SeatNumber))}); 
        // setActiveBus({...bus}); 
        setIsOpened(true);
    };

    const onSeatSelect = (seats: AvailableSeat[]) => {
        setSelectedSeats(seats);

        if (!isReturning) {
            const numberOfPAX = seats.length;
            updateFormData({ numberOfAdults: numberOfPAX });
        }
    }

    const onSeatSelectFinish = () => {
        setIsOpened(false);
        if (onlyDepartureSelected) {
            openNotification();
        }

        handleSelectBus(result, activeBus, selectedSeats);
    }

    const Footer = (
        <SeatFooterWrapper>
            <SeatsNumberWrapper>
                <StyledParagraph size="default">
                    Seats selected: {selectedSeats.length > 0 ? formatSelectedSeats(selectedSeats) : ''}
                </StyledParagraph>
            </SeatsNumberWrapper>

            <SpaceBetweenAlignContent>
                <StyledParagraph size="default">
                    Total for all passengers
                </StyledParagraph>

                <StyledTitle level={3}>
                    {formatNumberAsCurrency(totalFare || 0, result?.Currency)}
                </StyledTitle>
            </SpaceBetweenAlignContent>

            <Button block size="large" type="primary" onClick={onSeatSelectFinish} disabled={disableButton}>
                Continue
            </Button>
        </SeatFooterWrapper>
    )

    const getFormattedTime = (dateStr: string, fallbackTime: string) => {
        if (!dateStr) return fallbackTime;
        const m = moment(dateStr);
        return m.isValid() ? m.format("hh:mm a") : fallbackTime;
    };

    const formatDuration = (dur: string) => {
        if (!dur) return '';
        const parts = dur.split(':');
        if (parts.length === 2) {
            return `${parseInt(parts[0])}h ${parseInt(parts[1])}m`;
        }
        return dur;
    };

    const seatCounts = (bus: AvailableBus) => {
        const classCounts = { Normal: 0, Business: 0, VIP: 0 };
        bus.AvailableSeats?.forEach(seat => {
            if (seat.Class) {
                const lower = seat.Class.toLowerCase();
                if (lower === "business") {
                    classCounts.Business++;
                } else if (lower === "vip") {
                    classCounts.VIP++;
                } else {
                    classCounts.Normal++;
                }
            } else {
                classCounts.Normal++;
            }
        });

        return classCounts;
    };

    return (
        <BusDetailsWrapper ref={drawerContainerRef}>
            {
                isMobile ? (
                    <div>
                        {
                            result.AvailableBuses.map((bus, busIdx) => {
                                const totalSeats = bus.AvailableSeats?.length || bus.Capacity || 0;
                                const availableCount = bus.AvailableSeats?.filter(s => s.IsAvailable).length || 0;

                                const classCounts = seatCounts(bus);

                                const depTime = getFormattedTime(bus.EstimatedDepartureDate, bus.EstimatedDepartureTime || "N/A");
                                const arrTime = getFormattedTime(bus.EstimatedArrivalDate, bus.EstimatedArrivalTime || "N/A");
                                const durationStr = formatDuration(bus.Duration || "N/A");

                                return (
                                    <MobileBusCard onClick={() => onBusClick(bus)} key={busIdx} >
                                        {(cheapestFare === bus.TotalFare || busIdx === 0) && (
                                            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
                                                {cheapestFare === bus.TotalFare && (
                                                    <BestPriceTag>
                                                        <BestPriceIcon /> <span>Best price</span>
                                                    </BestPriceTag>
                                                )}
                                                {busIdx === 0 && (
                                                    <RecommendedTag>
                                                        <RecommendedIcon /> <span>Recommended</span>
                                                    </RecommendedTag>
                                                )}
                                            </div>
                                        )}

                                        <MobileItineraryRow>
                                            <MobileItineraryStation align="left">
                                                <span className="time">{depTime}</span>
                                                <span className="station">{result.DepartureTerminal}</span>
                                                <span className="sub-station">{bus.BoardingDetails?.Name}</span>
                                            </MobileItineraryStation>

                                            <MobileItineraryLine>
                                                <div className="line" />
                                                <div className="dot start" />
                                                <span className="duration">{durationStr}</span>
                                                <div className="dot end" />
                                            </MobileItineraryLine>

                                            <MobileItineraryStation align="right">
                                                <span className="time">{arrTime}</span>
                                                <span className="station">{result.ArrivalTerminal}</span>
                                                <span className="sub-station">{bus.DropOffDetails?.Name}</span>
                                            </MobileItineraryStation>
                                        </MobileItineraryRow>

                                        <MobileCardFooter>
                                            <MobileOperatorLogo src={bus.Operator?.Logo || getRoadTransportLogo(result.AgentName)} alt={result.AgentName} />

                                            <MobilePill variant="primary">
                                                <PassengerIcon /> {totalSeats} seats ({availableCount} left)
                                            </MobilePill>

                                            {classCounts.Normal > 0 && (
                                                <MobilePill variant="grey">
                                                    {classCounts.Normal} Normal
                                                </MobilePill>
                                            )}

                                            {classCounts.VIP > 0 && (
                                                <MobilePill variant="purple">
                                                    {classCounts.VIP} VIP
                                                </MobilePill>
                                            )}

                                            {classCounts.Business > 0 && (
                                                <MobilePill variant="green">
                                                    {classCounts.Business} Business
                                                </MobilePill>
                                            )}
                                        </MobileCardFooter>

                                        <div style={{ borderTop: "1px dashed #E5E7EB", background: LIGHT_PRIMARY, margin: "15px -16px -16px -16px", padding: "12px", borderRadius: "0 0 16px 16px", display: "flex", justifyContent: "flex-end" }}>
                                            <StyledTitle level={5}>
                                                <span style={{ color: "#9CA3AF", fontSize: "12px", fontWeight: "normal", marginRight: "4px" }}>From</span>
                                                {formatNumberAsCurrency(bus.TotalFare, result.Currency)}
                                            </StyledTitle>
                                        </div>
                                    </MobileBusCard>
                                )
                            })
                        }
                    </div>
                ) : (
                    <FlightCardsWrapper>
                        {
                            result.AvailableBuses.map((bus, index) => {

                                const totalSeats = bus.AvailableSeats?.length || bus.Capacity || 0;
                                const availableCount = bus.AvailableSeats?.filter(s => s.IsAvailable).length || 0;

                                const classCounts = seatCounts(bus);

                                const depTime = getFormattedTime(bus.EstimatedDepartureDate, bus.EstimatedDepartureTime || "N/A");
                                const arrTime = getFormattedTime(bus.EstimatedArrivalDate, bus.EstimatedArrivalTime || "N/A");
                                const durationStr = formatDuration(bus.Duration || "N/A");
                                return (
                                    <Row key={index}>
                                        <Col span={17} style={{ display: "flex" }}>
                                            <BusCard rightBordered>
                                                <Row>

                                                    <Col span={22}>
                                                        <BusCardContent>
                                                            {(cheapestFare === bus.TotalFare || index === 0) && (
                                                                <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
                                                                    {cheapestFare === bus.TotalFare && (
                                                                        <BestPriceTag>
                                                                            <BestPriceIcon /> <span>Best price</span>
                                                                        </BestPriceTag>
                                                                    )}
                                                                    {index === 0 && (
                                                                        <RecommendedTag>
                                                                            <RecommendedIcon /> <span>Recommended</span>
                                                                        </RecommendedTag>
                                                                    )}
                                                                </div>
                                                            )}
                                                            {/* <SpaceBetweenAlignContent>
                                                                <StyledTitle level={3}>
                                                                    {bus.BusType}
                                                                </StyledTitle>
                                                            </SpaceBetweenAlignContent> */}

                                                            <BusStepWrapper>

                                                                <BusCardImage>
                                                                    <img src={bus.Operator?.Logo || getRoadTransportLogo(result.AgentName)} alt={result.AgentName} width={50} />
                                                                    {/* <BusImage src={getBusTypeImage(bus.Capacity)} alt="bus" /> */}
                                                                </BusCardImage>
                                                                <div>
                                                                    <StyledTitle level={5}>
                                                                        {depTime}
                                                                    </StyledTitle>
                                                                    <StyledParagraph size="default" style={{ color: '#72737F' }}>
                                                                        {result.DepartureTerminal}
                                                                    </StyledParagraph>
                                                                </div>

                                                                <StepsContainer>
                                                                    <StyledParagraph align="center" size="small" style={{ color: '#72737F', marginBottom: '4px', fontWeight: 500 }}>
                                                                        {durationStr}
                                                                    </StyledParagraph>
                                                                    <StepsWrapper>
                                                                        <StepProgressLine />
                                                                        <StepItemWrapper>
                                                                            <StepItemContent>
                                                                                <BusIcon />
                                                                            </StepItemContent>
                                                                        </StepItemWrapper>

                                                                        <StepProgressLine />
                                                                    </StepsWrapper>
                                                                </StepsContainer>

                                                                <div>
                                                                    <StyledTitle level={5} align="left">
                                                                        {arrTime}
                                                                    </StyledTitle>
                                                                    <StyledParagraph size="default" align="left" style={{ color: '#72737F' }}>
                                                                        {result.ArrivalTerminal}
                                                                    </StyledParagraph>
                                                                </div>
                                                            </BusStepWrapper>

                                                            <TagsWrapper>
                                                                <Tag>
                                                                    <PassengerIcon />
                                                                    {totalSeats} Seats ({availableCount} left)
                                                                </Tag>
                                                                <Tag>
                                                                    <ClockIcon />
                                                                    {bus.EstimatedDepartureTime}
                                                                </Tag>

                                                            </TagsWrapper>
                                                            <TagsWrapper>

                                                                {classCounts.Normal > 0 && (
                                                                    <MobilePill variant="grey">
                                                                        {classCounts.Normal} Normal
                                                                    </MobilePill>
                                                                )}

                                                                {classCounts.VIP > 0 && (
                                                                    <MobilePill variant="purple">
                                                                        {classCounts.VIP} VIP
                                                                    </MobilePill>
                                                                )}

                                                                {classCounts.Business > 0 && (
                                                                    <MobilePill variant="green">
                                                                        {classCounts.Business} Business
                                                                    </MobilePill>
                                                                )}
                                                            </TagsWrapper>
                                                        </BusCardContent>
                                                    </Col>
                                                </Row>
                                            </BusCard>
                                        </Col>

                                        <Col span={7} style={{ display: "flex" }}>
                                            <BusCard leftBordered style={{ background: LIGHT_PRIMARY }}>
                                                <BusCardPriceContent>
                                                    <CentralizedContent>
                                                        <div>
                                                            <FareWrapper>
                                                                <StyledTitle level={4} align="center">
                                                                    <span style={{ color: "#9CA3AF", fontSize: "13px", fontWeight: "normal", marginRight: "4px" }}>From</span>
                                                                    {formatNumberAsCurrency(bus.TotalFare, result.Currency)}
                                                                </StyledTitle>
                                                            </FareWrapper>
                                                            <Button
                                                                type="primary"
                                                                block
                                                                onClick={() => onBusClick(bus)}
                                                            >
                                                                Select seats
                                                            </Button>
                                                        </div>
                                                    </CentralizedContent>
                                                </BusCardPriceContent>
                                            </BusCard>
                                        </Col>
                                    </Row>
                                )
                            })
                        }
                    </FlightCardsWrapper>
                )
            }

            <Drawer
                title="Seat selection"
                placement={isMobile ? "bottom" : "right"}
                onClose={() => setIsOpened(false)}
                open={isOpened}
                footer={Footer}
                forceRender={true}
                height="100%"
                getContainer={drawerContainerRef.current as HTMLElement}
            >
                {
                    activeBus && <SeatSelection bus={activeBus} onSeatSelect={onSeatSelect} limitSelection={isReturning} />
                }
            </Drawer>
        </BusDetailsWrapper>
    );
}

export default BusDetails;
